import React, { AnchorHTMLAttributes } from 'react';
import { makeStyles } from 'tss-react/mui';
import { Link as ReactRouterLink } from 'react-router-dom';

import { NormalizedAlgoliaListing } from 'ha/models/Listing/types';
import { Card, Link } from '@ha/dsl';
import { useRoomPageLink } from 'ha/modules/Nextjs';
import { buildParams } from '@ha/search-query';
import { useFeatureFlags } from 'ha/modules/FeatureFlags';
import { useIntl } from 'ha/i18n';
import { useMulFeatureFlags } from 'ha/modules/MultiUnitListings/hooks';

import { PhotoGalleryWithControls } from './PhotoGalleryWithControls';
import { PhotoGalleryWithControlsSwiper } from './PhotoGalleryWithControlsSwiper';
import { Info } from './Info';

const useStyles = makeStyles()(theme => ({
  card: {
    '&:hover': {
      backgroundColor: theme.palette.grey[50],
    },
    height: '100%',
    width: '100%',
  },
  cardLink: {
    textDecoration: 'none',
  },
}));

interface Props {
  room: NormalizedAlgoliaListing;
  isLazy?: boolean;
  scrollableElement?: HTMLElement | 'window';
  bottomOffset?: string;
  withGallery?: boolean;
  withFavorites?: boolean;
  noFollow?: boolean;
  onMouseEnter?: (id: number) => void;
  onMouseLeave?: (id: number) => void;
  onCardClick?: () => void;
  queryParams?: {
    startDate: string | null;
    endDate: string | null;
  };
  linkTarget?: '_blank';
  // used in analytics
  cardLocation?: string;
  isImperialSystem: boolean;
}

export const ListingCard = React.forwardRef<HTMLDivElement, Props>(
  (props, ref) => {
    const {
      onMouseEnter,
      onMouseLeave,
      room,
      withGallery,
      withFavorites,
      isLazy,
      scrollableElement,
      bottomOffset = '-50%',
      onCardClick,
      queryParams = {},
      noFollow,
      linkTarget,
      cardLocation = 'unspecified',
      isImperialSystem,
    } = props;
    const { classes } = useStyles();

    const { showNewGallery } = useFeatureFlags();
    const isNewGallery = showNewGallery === 'on';

    const { isTenantMULFlowEnabled } = useMulFeatureFlags(room.country);
    const { urlResolver } = useIntl();

    const redirectPath = React.useMemo(
      () =>
        isTenantMULFlowEnabled && room.unitTypePath
          ? room.unitTypePath
          : room.listingPath,
      [isTenantMULFlowEnabled, room],
    );
    const redirectLink = useRoomPageLink(redirectPath, queryParams);

    const handleMouseEnter = React.useCallback(() => {
      if (onMouseEnter) {
        onMouseEnter(room.id);
      }
    }, [onMouseEnter, room.id]);

    const handleMouseLeave = React.useCallback(() => {
      if (onMouseLeave) {
        onMouseLeave(room.id);
      }
    }, [onMouseLeave, room.id]);

    const linkProps: AnchorHTMLAttributes<HTMLAnchorElement> = {
      target: linkTarget,
      rel: noFollow ? 'noopener noreferrer' : undefined,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onClick: onCardClick,
      itemScope: true,
      itemType: 'http://schema.org/Offer',
      className: classes.cardLink,
    };

    const cardElement = (
      <Card elevation={1} className={classes.card} ref={ref}>
        <PhotoGalleryWithControls
          room={room}
          isLazy={isLazy}
          scrollableElement={scrollableElement}
          bottomOffset={bottomOffset}
          cardLocation={cardLocation}
          withGallery={withGallery}
          withFavorites={withFavorites}
        />
        <Info room={room} isImperialSystem={isImperialSystem} />
      </Card>
    );

    const cardElementWithSwiper = (
      <Card elevation={1} className={classes.card} ref={ref}>
        <PhotoGalleryWithControlsSwiper
          room={room}
          isLazy={isLazy}
          scrollableElement={scrollableElement}
          bottomOffset={bottomOffset}
          cardLocation={cardLocation}
          withGallery={withGallery}
          withFavorites={withFavorites}
        />
        <Info room={room} isImperialSystem={isImperialSystem} />
      </Card>
    );

    return linkTarget === '_blank' ? (
      <Link {...linkProps} data-test-locator="Listing Card" to={redirectLink}>
        {isNewGallery ? cardElementWithSwiper : cardElement}
      </Link>
    ) : (
      <ReactRouterLink
        {...linkProps}
        data-test-locator="Listing Card"
        to={{
          pathname: urlResolver.formatHrefURL(redirectPath),
          search: buildParams(queryParams),
        }}
        state={{
          cardLocation,
        }}
      >
        {isNewGallery ? cardElementWithSwiper : cardElement}
      </ReactRouterLink>
    );
  },
);
